import { SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useGetApiV1PersonSharedAccountTypeDropdown,
  useGetApiV1PersonSharedFinancialPaymentMethodDropdown,
} from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';

import { numToStr, strToNum, type TabProps } from '../personFormSchema';

export interface FinancialTabProps extends TabProps {
  /** Quando true, exibe skeleton enquanto os dados financeiros opcionais carregam */
  readonly isLoading?: boolean;
}

export function FinancialTab({ control, register, isLoading = false }: FinancialTabProps) {
  const { t } = useTranslation('personManagement');

  const { data: paymentMethodOptions = [] } =
    useGetApiV1PersonSharedFinancialPaymentMethodDropdown();
  const { data: accountTypeOptions = [] } = useGetApiV1PersonSharedAccountTypeDropdown();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-mini">
        <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
          <Skeleton className="h-10 w-full rounded-sm" />
          <Skeleton className="h-10 w-full rounded-sm" />
        </div>
        <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
          <Skeleton className="h-10 w-full rounded-sm" />
          <Skeleton className="h-10 w-full rounded-sm" />
        </div>
        <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
          <Skeleton className="h-10 w-full rounded-sm" />
          <Skeleton className="h-10 w-full rounded-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-mini">
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <TextField
          fullWidth
          size="md"
          label={t('personForm.financial.bankName')}
          placeholder={t('personForm.financial.bankName')}
          {...register('financialBankName')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.financial.agency')}
          placeholder={t('personForm.financial.agency')}
          {...register('financialAgency')}
        />
      </div>
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <TextField
          fullWidth
          size="md"
          label={t('personForm.financial.checkingAccount')}
          placeholder={t('personForm.financial.checkingAccount')}
          {...register('financialCheckingAccount')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.financial.savingAccount')}
          placeholder={t('personForm.financial.savingAccount')}
          {...register('financialSavingAccount')}
        />
      </div>
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <Controller
          name="financialPaymentMethod"
          control={control}
          render={({ field }) => (
            <SelectInput
              size="md"
              label={t('personForm.financial.paymentMethod')}
              placeholder={t('personForm.financial.paymentMethod')}
              value={numToStr(field.value)}
              onValueChange={v => field.onChange(strToNum(v))}
            >
              {paymentMethodOptions.map(({ key, value }) => (
                <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                  {value ?? ''}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
        <Controller
          name="financialAccountType"
          control={control}
          render={({ field }) => (
            <SelectInput
              size="md"
              label={t('personForm.financial.accountType')}
              placeholder={t('personForm.financial.accountType')}
              value={numToStr(field.value)}
              onValueChange={v => field.onChange(strToNum(v))}
            >
              {accountTypeOptions.map(({ key, value }) => (
                <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                  {value ?? ''}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
      </div>
    </div>
  );
}
